import { useState, forwardRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MapPin, X, Lock } from "lucide-react";
import { Reveal as SectionReveal, Chapter } from "../shared";
import { CompileOverlay } from "./CompileOverlay";
import { Reveal } from "./Reveal";
import { BirthDatePicker } from "./BirthDatePicker";
import { computeChart, sourceId } from "../../../lib/astro";

const Label = ({ children }) => (
  <p className="font-mono-brand text-[11px] font-bold tracking-[2px] text-[#737687] uppercase mb-3">{children}</p>
);

const inputCls =
  "block w-full min-w-0 max-w-full appearance-none rounded-xl bg-[#1B1C1F] border border-[#2A2C30] px-4 py-3 text-sm text-[#F2F0EB] placeholder:text-[#737687] caret-[#FF4D0A] focus:outline-none focus:border-[#F2F0EB]/40 transition-colors [color-scheme:dark]";

const CtaBtn = ({ disabled, children, testId, onClick, type = "button" }) => (
  <button
    type={type}
    onClick={onClick}
    disabled={disabled}
    className="w-full rounded-xl bg-[#FF4D0A] text-[#0B0B0C] font-display font-bold text-[13px] sm:text-sm tracking-wide uppercase px-6 py-3.5 sm:py-4 hover:brightness-110 transition disabled:opacity-40 disabled:cursor-not-allowed"
    data-testid={testId}
  >
    {children}
  </button>
);

export const SecureCode = forwardRef((_, ref) => {
  const [step, setStep] = useState(1);
  const [name, setName] = useState("");
  const [date, setDate] = useState("1995-06-15");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [geoError, setGeoError] = useState(false);
  const [place, setPlace] = useState(null);
  const [time, setTime] = useState("12:00");
  const [timeUnknown, setTimeUnknown] = useState(false);
  const [compiling, setCompiling] = useState(false);
  const [chart, setChart] = useState(null);
  const [sid, setSid] = useState(null);

  const search = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    setSearching(true);
    setGeoError(false);
    setResults(null);
    try {
      const res = await fetch(
        `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(query.trim())}&count=5`
      );
      const data = await res.json();
      if (data.results?.length) setResults(data.results);
      else setGeoError(true);
    } catch {
      setGeoError(true);
    } finally {
      setSearching(false);
    }
  };

  const compile = () => {
    const input = {
      name, date, time, timeUnknown,
      lat: place.latitude, lon: place.longitude, tz: place.timezone,
    };
    setChart(computeChart({ date, time, tz: place.timezone, lat: place.latitude, lon: place.longitude, timeUnknown }));
    setSid(sourceId(input));
    setCompiling(true);
  };

  const coordText = (p) =>
    `${Math.abs(p.latitude).toFixed(2)}\u00B0${p.latitude >= 0 ? "N" : "S"} \u00B7 ${Math.abs(p.longitude).toFixed(2)}\u00B0${p.longitude >= 0 ? "E" : "W"}`;

  return (
    <section ref={ref} id="secure" className="py-14 sm:py-24 lg:py-32 bg-[#0B0B0C]" data-testid="secure-code-section">
      <div className="mx-auto max-w-[480px] px-5">
        <SectionReveal>
          <Chapter num="02" label="Your Source Code" />
        </SectionReveal>

        <AnimatePresence mode="wait">
          {step === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              data-testid="secure-step-1"
            >
              <h2 className="font-display font-bold text-[26px] sm:text-[30px] leading-tight text-[#F2F0EB]" data-testid="secure-step1-headline">
                Your Source Code starts here
              </h2>
              <p className="font-mono-brand text-[11px] font-bold tracking-[2px] text-[#737687] mt-2.5 sm:mt-3">1 OF 3</p>
              <p className="mt-2.5 sm:mt-3 text-[13px] sm:text-sm text-[#A3A4A3] leading-relaxed">
                Your birth date and time set the starting point we build your
                rhythm from. Precise to the minute, computed on your phone, so
                nothing about your day is a guess.
              </p>
              <div className="mt-6 sm:mt-8 rounded-2xl bg-[#141416] border border-[#2A2C30] p-4 space-y-6 sm:space-y-8">
                <div>
                  <Label>Name</Label>
                  <input
                    type="text"
                    maxLength={50}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Full name"
                    className={inputCls}
                    data-testid="secure-name-input"
                  />
                </div>
                <div>
                  <Label>Birth date</Label>
                  <BirthDatePicker value={date} onChange={setDate} />
                </div>
              </div>
              <p className="mt-3 flex items-center gap-1.5 text-[11px] text-[#737687]" data-testid="secure-privacy-note">
                <Lock size={11} className="text-[#FF4D0A] shrink-0" />
                Computed on your device. Your birth data never leaves the app.
              </p>
              <div className="mt-6">
                <CtaBtn disabled={!name.trim()} onClick={() => setStep(2)} testId="secure-continue-button">
                  Continue
                </CtaBtn>
                {!name.trim() && (
                  <p className="mt-2 text-center text-[11px] text-[#737687]" data-testid="secure-continue-hint">
                    Add your name to continue
                  </p>
                )}
              </div>
            </motion.div>
          )}

          {step === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              data-testid="secure-step-2"
            >
              <p className="font-mono-brand text-[11px] font-bold tracking-[2px] text-[#737687]">2 OF 3</p>
              <h2 className="font-display font-bold text-[26px] sm:text-[30px] leading-tight text-[#F2F0EB] mt-2.5 sm:mt-3" data-testid="secure-step2-headline">
                Where you were born
              </h2>
              <p className="mt-2.5 sm:mt-3 text-[13px] sm:text-sm text-[#A3A4A3] leading-relaxed">
                One city becomes exact coordinates: your light, your daylight
                hours and your time zone from day one. How you land in a room
                depends on this.
              </p>
              <div className="mt-6 sm:mt-8 rounded-2xl bg-[#141416] border border-[#2A2C30] p-4 space-y-6 sm:space-y-8">
                <div>
                  <Label>Birth place</Label>
                  {place ? (
                    <div className="flex items-center justify-between rounded-xl bg-[#1B1C1F] border border-[#2A2C30] px-4 py-3" data-testid="secure-place-locked">
                      <div>
                        <p className="text-sm text-[#F2F0EB]">
                          {place.name}
                          {place.admin1 ? `, ${place.admin1}` : ""}, {place.country}
                        </p>
                        <p className="font-mono-brand text-[11px] text-[#737687] mt-0.5">{coordText(place)}</p>
                      </div>
                      <button
                        onClick={() => { setPlace(null); setResults(null); setQuery(""); }}
                        className="text-[#737687] hover:text-[#F2F0EB] transition-colors p-1"
                        data-testid="secure-place-clear"
                        aria-label="Clear selected place"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ) : (
                    <form onSubmit={search}>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={query}
                          onChange={(e) => setQuery(e.target.value)}
                          placeholder="City, country"
                          className={inputCls}
                          data-testid="secure-city-input"
                        />
                        <button
                          type="submit"
                          disabled={searching}
                          className="rounded-xl border border-[#2A2C30] px-4 text-sm text-[#F2F0EB] hover:border-[#F2F0EB]/40 transition-colors disabled:opacity-50"
                          data-testid="secure-city-search-button"
                        >
                          {searching ? "\u2026" : "Search"}
                        </button>
                      </div>
                      {geoError && (
                        <p className="mt-3 text-xs text-[#A3A4A3]" data-testid="secure-geo-error">
                          Couldn't find that one. Try &ldquo;City, Country&rdquo; instead.
                        </p>
                      )}
                      {results && (
                        <div className="mt-3 rounded-xl border border-[#2A2C30] divide-y divide-[#2A2C30] overflow-hidden" data-testid="secure-geo-results">
                          {results.map((r) => (
                            <button
                              key={r.id}
                              type="button"
                              onClick={() => { setPlace(r); setResults(null); }}
                              className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm text-[#F2F0EB] bg-[#1B1C1F] hover:bg-[#222327] transition-colors"
                              data-testid={`secure-geo-result-${r.id}`}
                            >
                              <MapPin size={14} className="text-[#737687] shrink-0" />
                              {r.name}
                              {r.admin1 ? `, ${r.admin1}` : ""}, {r.country}
                            </button>
                          ))}
                        </div>
                      )}
                    </form>
                  )}
                </div>
                <div>
                  <Label>Birth time</Label>
                  {!timeUnknown && (
                    <input
                      type="time"
                      value={time}
                      onChange={(e) => setTime(e.target.value)}
                      className={inputCls}
                      data-testid="secure-time-input"
                    />
                  )}
                  <label className="mt-3 flex items-center gap-2.5 text-sm text-[#A3A4A3] cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={timeUnknown}
                      onChange={(e) => setTimeUnknown(e.target.checked)}
                      className="h-4 w-4 rounded accent-[#FF4D0A]"
                      data-testid="secure-time-unknown-checkbox"
                    />
                    I don't know my birth time
                  </label>
                </div>
              </div>
              <div className="mt-6">
                <CtaBtn disabled={!place} onClick={compile} testId="secure-compile-button">
                  Create my Source Code
                </CtaBtn>
              </div>
              <button
                onClick={() => setStep(1)}
                className="mt-4 font-mono-brand text-[11px] tracking-[1.5px] text-[#737687] hover:text-[#F2F0EB] transition-colors uppercase"
                data-testid="secure-back-button"
              >
                &larr; Back
              </button>
            </motion.div>
          )}

          {step === 3 && (
            <motion.div
              key="step3"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.4 }}
            >
              <Reveal firstName={name.trim().split(/\s+/)[0]} chart={chart} sid={sid} timeUnknown={timeUnknown} />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <AnimatePresence>
        {compiling && (
          <CompileOverlay
            onDone={() => {
              setCompiling(false);
              setStep(3);
            }}
          />
        )}
      </AnimatePresence>
    </section>
  );
});
